import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ACTIONS, EVENTS, Joyride, STATUS } from "react-joyride";
import {
  reportWalkthroughActivity,
  WalkthroughEvent,
  type WalkthroughTourKey,
} from "../../api/activityFunctions";
import { useChatFeedbackLock } from "../FarChat/ChatFeedbackOverlays";

type JoyrideProps = React.ComponentProps<typeof Joyride>;

interface TrackedJoyrideProps extends JoyrideProps {
  tourKey: WalkthroughTourKey;
}

/**
 * Adds privacy-minimal lifecycle telemetry to every product Joyride tour.
 *
 * The wrapper leaves the individual components responsible for their existing
 * local `useTourSeen` pause/resume behavior. It only observes a real run and
 * Joyride's terminal results, so intermediate steps are never reported.
 */
export function TrackedJoyride({
  tourKey,
  run,
  onEvent,
  ...joyrideProps
}: TrackedJoyrideProps): JSX.Element {
  const terminalEventSent = useRef(false);
  const wasRunning = useRef(false);
  const locked = useChatFeedbackLock();
  const currentIndex = useRef(joyrideProps.stepIndex ?? joyrideProps.initialStepIndex ?? 0);
  const [resumeIndex, setResumeIndex] = useState<number>();

  useLayoutEffect(() => {
    if (locked && run) setResumeIndex(currentIndex.current);
    else if (!run) setResumeIndex(undefined);
  }, [locked, run]);

  useEffect(() => {
    if (run && !wasRunning.current) {
      terminalEventSent.current = false;
      void reportWalkthroughActivity(WalkthroughEvent.Started, tourKey);
    }
    wasRunning.current = Boolean(run);
  }, [run, tourKey]);

  const handleEvent = useCallback(
    (...args: Parameters<NonNullable<JoyrideProps["onEvent"]>>) => {
      const [data] = args;
      currentIndex.current = data.index;
      const isCompleted =
        data.status === STATUS.FINISHED && data.type === EVENTS.TOUR_END;
      const isDismissed =
        data.status === STATUS.SKIPPED ||
        (data.action === ACTIONS.CLOSE && data.type === EVENTS.TOUR_END);

      if (!terminalEventSent.current && (isCompleted || isDismissed)) {
        terminalEventSent.current = true;
        void reportWalkthroughActivity(
          isCompleted
            ? WalkthroughEvent.Completed
            : WalkthroughEvent.Dismissed,
          tourKey
        );
      }

      onEvent?.(...args);
    },
    [onEvent, tourKey]
  );

  // Joyride restarts uncontrolled tours at initialStepIndex when run returns
  // true. Resume the interrupted step while leaving owner intent/telemetry intact.
  return <Joyride {...joyrideProps} run={locked ? false : run}
    initialStepIndex={joyrideProps.stepIndex === undefined
      ? resumeIndex ?? joyrideProps.initialStepIndex : joyrideProps.initialStepIndex}
    onEvent={handleEvent} />;
}
